import { API_GATEWAY_URL } from '../constants/config';
import { isErrorBody } from '../errors/ErrorHandler';
import { GenericError } from '../errors/GenericError';
import { withStandardHandlers } from './utils';
export const USER_API_URL = `${API_GATEWAY_URL}/user`;
export const USERS_API_URL = `${API_GATEWAY_URL}/users`;
export const AGREE_TO_TOS_API_URL = `${API_GATEWAY_URL}/agree-to-tos`;
export const REGISTER_USER_API_URL = `${API_GATEWAY_URL}/register-auth0-account`;
/**
 * Get user information
 * @param accessToken - The access token for authentication.
 */
export const getUser = (accessToken) => {
    const fn = () => fetch(USER_API_URL, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
    });
    return withStandardHandlers(fn, 'errors.user.fetch')();
};
/**
 * Get the admin user list — one keyset page at a time.
 *
 * Admin-only endpoint. Supports the HubSpot-only filter, Auth0 email search, subscription
 * end-date sort, and keyset pagination via `lastEvaluatedKey`.
 *
 * @param accessToken Access token
 * @param params Filter / pagination inputs
 * @returns The page of users plus the cursor (`last_evaluated_key`) for the next page
 */
export const getUserList = (accessToken, params) => {
    const searchParams = new URLSearchParams({
        hubspot_only: String(params.hubspotOnly),
        hubspot_properties: String(params.hubspotProperties ?? true),
    });
    if (params.lastEvaluatedKey) {
        searchParams.append('last_evaluated_key', JSON.stringify(params.lastEvaluatedKey));
    }
    if (params.email) {
        searchParams.append('email', params.email);
    }
    if (params.baseFeatureEndAt) {
        searchParams.append('base_feature_end_at', params.baseFeatureEndAt);
    }
    if (params.limit) {
        searchParams.append('limit', String(params.limit));
    }
    const fn = () => fetch(`${USERS_API_URL}?${searchParams.toString()}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
    });
    // Keep the whole payload (results + keyset cursor); do not unwrap `results`.
    return withStandardHandlers(fn, 'errors.user.fetch', false)();
};
/**
 * Update user information
 *
 * @param accessToken Access token
 * @param user User information
 * @returns Updated user information
 */
export const patchUser = (accessToken, user) => {
    const fn = () => fetch(USER_API_URL, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
            sheet_rows_visibility: user.sheet_rows_visibility,
            sheet_cols_visibility: user.sheet_cols_visibility,
            sheet_diagram_visibility: user.sheet_diagram_visibility,
            sheet_models_visibility: user.sheet_models_visibility,
            sheet_rows_tolerance_type: user.sheet_rows_tolerance_type,
            sheet_order_type_of_items: user.sheet_order_type_of_items,
            cover_thickness_value_type: user.cover_thickness_value_type,
            display_config: user.display_config,
        }),
    });
    return withStandardHandlers(fn, 'errors.user.fetch')();
};
/**
 * Update user information
 *
 * @param accessToken Access token
 * @param users User information
 * @returns Updated user information
 */
export const patchUsers = (accessToken, users) => {
    const fn = () => fetch(USERS_API_URL, {
        method: 'PATCH',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(users.map((user) => ({
            user_id: user.user_id,
            params: {
                hubspot_id: user.hubspot_id,
            },
        }))),
    });
    return withStandardHandlers(fn, 'errors.user.fetch')();
};
/**
 * Agree to the terms of service
 * @param accessToken Access token
 * @param agreedTosVersion Agreed ToS version
 * @returns Updated user information
 */
export const agreeToTos = (accessToken, agreedTosVersion) => {
    const fn = () => fetch(AGREE_TO_TOS_API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
            'X-Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
            agreed_tos_version: agreedTosVersion,
        }),
    });
    return withStandardHandlers(fn, 'errors.user.fetch')();
};
/**
 * Register a free viewer account.
 *
 * Public endpoint — it takes no access token because the caller is not signed in yet. On
 * success the backend emails the address a password-setup link; the account only exists
 * once that link is used.
 *
 * The response is handled here rather than through `withStandardHandlers`: this endpoint
 * answers 201 with no body at all, and the shared wrapper parses every successful response
 * as JSON, which throws on an empty one.
 *
 * @param emailAddress Address the account is created for
 * @param name Full name, collected only while self-managed subscriptions are enabled
 * @param companyName Company name, collected only while self-managed subscriptions are enabled
 */
export const registerUser = async (emailAddress, name, companyName) => {
    const res = await fetch(REGISTER_USER_API_URL, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            responseType: 'json',
        },
        body: JSON.stringify({
            email_address: emailAddress,
            name,
            company_name: companyName,
        }),
    });
    if (res.ok)
        return;
    const body = await res.json().catch(() => undefined);
    if (body instanceof Object && isErrorBody(body)) {
        throw new GenericError('errors.user.register', body.status_id);
    }
    throw new GenericError('errors.user.register', res.status);
};
